/**
 * CEO Command Center - Configuration Constants
 *
 * This file contains all configuration values, thresholds, and magic numbers
 * previously scattered throughout the codebase. Centralizing these makes
 * the system easier to tune and maintain.
 *
 * @module config/constants
 * @version 11.0.0
 */

// ============================================
// APPLICATION CONFIGURATION
// ============================================

/**
 * Application version and metadata
 */
export const APP_CONFIG = {
  /** Current application version */
  VERSION: '11.0.0',

  /** Previous version (for migration reference) */
  PREVIOUS_VERSION: '10.2.0',
  
  /** Local storage keys */
  STORAGE_KEYS: {
    AUTH: 'sentiance_ceo_auth',
    CONFIG: 'sentiance_ceo_config',
    CHAT: 'sentiance_ceo_chat',
    AI_PROMPT: 'sentiance_ai_system_prompt',
    KNOWLEDGE_BASE: 'sentiance_ai_knowledge_base',
    AIA_TASKS: 'sentiance_aia_tasks',
    BOARD_CONFIG: 'sentiance_board_config',
    DECK_SECTIONS: 'sentiance_deck_sections',
    INITIATIVES: 'sentiance_initiatives',
    BOARD_MEMBERS: 'sentiance_board_members',
    MEETING_HISTORY: 'sentiance_meeting_history',
    INVESTORS: 'sentiance_investors',
    MARKET_NEWS: 'sentiance_market_news',
    NEWS_API_CONFIG: 'sentiance_news_api_config',
    MAU_DATA: 'sentiance_mau_data',
    CUSTOMER_MAU: 'sentiance_customer_mau',
    MAU_SUMMARY: 'sentiance_mau_summary',
  },
};

// ============================================
// API CONFIGURATION
// ============================================

/**
 * API endpoints and configuration
 * In production, BASE_URL should point to the Cloudflare Worker proxy
 * BASE_URL should NOT include trailing /api - paths are appended with /api/...
 */
export const API_CONFIG = {
  /** Base URL for API calls (Cloudflare Worker proxy for OpenAI/News) */
  BASE_URL:
    typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL
      ? import.meta.env.VITE_API_URL
      : 'https://ceo-command-center-api-v2.ceoccsentiance.workers.dev',

  /** Request timeout in milliseconds */
  TIMEOUT: 30000,

  /** Retry configuration for failed requests */
  RETRY: {
    MAX_ATTEMPTS: 3,
    BASE_DELAY_MS: 1000,
    MAX_DELAY_MS: 10000,
  },

  /** Cache TTLs in seconds (for external API responses) */
  CACHE_TTL: {
    NEWS: 300,
    AI_RESPONSE: 0, // Don't cache AI responses
  },
};

/**
 * SQLite table names
 * Used for local-first storage
 */
export const SQLITE_TABLES = {
  TASKS: 'tasks',
  DEALS: 'deals',
  CLIENTS: 'clients',
  OWNERS: 'owners',
  CASHFLOW: 'cashflow',
  INVESTORS: 'investors',
  MAU_DATA: 'mau_data',
};

// ============================================
// SIGNAL DETECTION THRESHOLDS
// ============================================

/**
 * Thresholds for signal detection engine
 * These values determine when items become "signals" requiring CEO attention
 */
export const SIGNAL_THRESHOLDS = {
  /** Deal size thresholds (EUR) */
  DEAL_SIZE_HIGH: 500000,        // €500k - Large deal
  DEAL_SIZE_CRITICAL: 1000000,   // €1M - Critical deal needing CEO attention
  
  /** Cash runway thresholds (months) */
  CASH_RUNWAY_WARNING: 9,        // 9 months - Start paying attention
  CASH_RUNWAY_CRITICAL: 6,       // 6 months - Urgent action needed
  
  /** Blocked task thresholds (days) */
  BLOCKED_TASK_WARNING: 3,       // 3 days - Task needs attention
  BLOCKED_TASK_CRITICAL: 7,      // 7 days - Escalate to CEO
  
  /** Client churn risk thresholds */
  CHURN_RISK_ARR: 100000,        // €100k ARR - High-value client at risk
  CHURN_DAYS_WARNING: 30,        // 30 days to contract expiry
  CHURN_DAYS_CRITICAL: 14,       // 14 days to contract expiry
  
  /** Deal progress thresholds (days) */
  DEAL_STUCK_DAYS: 30,           // 30 days with no progress
  
  /** Task overdue thresholds (days) */
  OVERDUE_WARNING: -3,           // 3 days overdue
  OVERDUE_CRITICAL: -7,          // 7 days overdue
  
  /** Investor follow-up (days since last contact) */
  INVESTOR_FOLLOWUP: 14,         // 2 weeks without contact
  INVESTOR_FOLLOWUP_URGENT: 21,  // 3 weeks without contact
};

/**
 * Weights for urgency score calculation
 * Must sum to 1.0 (100%)
 */
export const URGENCY_WEIGHTS = {
  TIME_SENSITIVITY: 0.35,      // 35% - How soon is the deadline?
  REVENUE_IMPACT: 0.30,        // 30% - What's the financial impact?
  STRATEGIC_ALIGNMENT: 0.20,   // 20% - Is this a CEO priority?
  DEPENDENCY_CHAIN: 0.15,      // 15% - Does this block other work?
};

/**
 * Signal level thresholds based on urgency score
 */
export const SIGNAL_LEVELS = {
  CRITICAL: 85,    // Score >= 85 = Critical
  HIGH: 70,        // Score >= 70 = High
  MEDIUM: 50,      // Score >= 50 = Medium
  // Below 50 = Low
};

// ============================================
// DOMAIN CONFIGURATION
// ============================================

/**
 * Business domains configuration
 * Each domain represents a major area of CEO oversight
 */
export const DOMAIN_CONFIG = {
  EMG: {
    name: 'EMG',
    fullName: 'Executive Management Group',
    icon: '👥',
    color: '#2D2D2D',
    uploadLabel: 'Upload EMG Minutes',
    description: 'Weekly executive team meetings and decisions',
  },
  PRODUCT: {
    name: 'Product',
    fullName: 'Product Development',
    icon: '🔧',
    color: '#1E3A5F',
    uploadLabel: 'Upload Meeting Transcript',
    description: 'Product roadmap, features, and technical decisions',
  },
  GROWTH: {
    name: 'Growth',
    fullName: 'Sales & Growth',
    icon: '📈',
    color: '#E85D4C',
    uploadLabel: 'Upload Meeting Transcript',
    description: 'Sales pipeline, marketing, and expansion',
  },
  OPERATIONS: {
    name: 'Operations',
    fullName: 'Operations',
    icon: '⚙️',
    color: '#22C55E',
    uploadLabel: 'Upload Meeting Transcript',
    description: 'Operational efficiency and processes',
  },
  FINANCE: {
    name: 'Finance',
    fullName: 'Finance & Legal',
    icon: '💵',
    color: '#7C3AED',
    uploadLabel: 'Upload Meeting Transcript',
    description: 'Financial planning, cash flow, and legal',
  },
  AIA: {
    name: 'AI Automation',
    fullName: 'AI & Automation',
    icon: '🤖',
    color: '#0EA5E9',
    uploadLabel: 'Upload Meeting Transcript',
    description: 'AI automation projects and initiatives',
  },
  MAU: {
    name: 'MAU Analytics',
    fullName: 'MAU Analytics',
    icon: '📊',
    color: '#F59E0B',
    uploadLabel: 'Upload MAU Master',
    description: 'Monthly active users tracking',
  },
  FUNDRAISING: {
    name: 'Fundraising',
    fullName: 'Fundraising',
    icon: '💰',
    color: '#10B981',
    uploadLabel: 'Upload Investor List',
    description: 'Investor relations and fundraising',
  },
  BOARD: {
    name: 'Board',
    fullName: 'Board Relations',
    icon: '🏛️',
    color: '#6366F1',
    uploadLabel: 'Upload Board Docs',
    description: 'Board meetings and governance',
  },
};

// ============================================
// TASK & DEAL CONFIGURATION
// ============================================

/**
 * Available task statuses
 */
export const TASK_STATUSES = ['Not Started', 'In Progress', 'Done'];

/**
 * Task priority levels
 */
export const TASK_PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

/**
 * Deal stages (sales pipeline)
 */
export const DEAL_STAGES = [
  'Qualification',
  'Discovery', 
  'Proposal',
  'Negotiation',
  'Verbal Commit',
  'Closed Won',
  'Closed Lost',
];

/**
 * Investor/fundraising stages
 */
export const INVESTOR_STAGES = [
  'Research',
  'Outreach',
  'Meeting',
  'Due Diligence',
  'Term Sheet',
  'Closed',
  'Passed',
] as const;

/**
 * Extended investor stages for detailed tracking
 */
export const INVESTOR_STAGES_EXTENDED = [
  'Research',           // Identifying fit
  'Intro Requested',    // Asked for warm intro
  'Outreach',           // Cold/warm outreach sent
  'Deck Sent',          // Shared pitch deck
  'First Meeting',      // Initial call/meeting
  'Partner Meeting',    // Meeting with partners
  'Due Diligence',      // Active DD
  'Term Sheet',         // Received/negotiating terms
  'Legal',              // Docs in progress
  'Closed',             // Committed
  'Passed',             // Declined
  'Nurture',            // Keep warm for future
] as const;

/**
 * Investor stage configuration with colors and icons
 */
export const INVESTOR_STAGE_CONFIG: Record<string, { color: string; icon: string; description: string }> = {
  'Research': { color: '#6B7280', icon: '🔬', description: 'Researching investor fit' },
  'Intro Requested': { color: '#8B5CF6', icon: '🤝', description: 'Requested warm introduction' },
  'Outreach': { color: '#3B82F6', icon: '📧', description: 'Initial outreach sent' },
  'Deck Sent': { color: '#0EA5E9', icon: '📊', description: 'Pitch deck shared' },
  'First Meeting': { color: '#14B8A6', icon: '☕', description: 'Initial meeting scheduled' },
  'Meeting': { color: '#8B5CF6', icon: '🤝', description: 'Meeting scheduled' },
  'Partner Meeting': { color: '#A855F7', icon: '👥', description: 'Partner meeting' },
  'Due Diligence': { color: '#F59E0B', icon: '📋', description: 'Active due diligence' },
  'Term Sheet': { color: '#10B981', icon: '📑', description: 'Term sheet received' },
  'Legal': { color: '#22C55E', icon: '⚖️', description: 'Legal docs in progress' },
  'Closed': { color: '#22C55E', icon: '✅', description: 'Investment committed' },
  'Passed': { color: '#EF4444', icon: '⏭️', description: 'Declined to invest' },
  'Nurture': { color: '#6B7280', icon: '🌱', description: 'Keeping warm for future' },
};

// ============================================
// BOARD READINESS CONFIGURATION
// ============================================

/**
 * Board readiness scoring weights
 */
export const BOARD_READINESS_CONFIG = {
  /** Score penalties for various issues */
  PENALTIES: {
    MISSING_CASHFLOW: 20,       // No recent cash flow data
    NO_ACTIVE_DEALS: 20,        // Empty pipeline
    LOW_PIPELINE: 10,           // Pipeline < €1M weighted
    OUTDATED_MAU: 15,           // MAU data needs update
    TOO_MANY_BLOCKED: 10,       // >5 blocked tasks
    TOO_MANY_OVERDUE: 5,        // >10 overdue tasks
    TASKS_NO_OWNER: 10,         // >5 tasks without owners
    AT_RISK_CLIENTS: 10,        // >2 at-risk clients
  },
  
  /** Thresholds for status */
  STATUS_THRESHOLDS: {
    READY: 80,                  // >= 80 = Board Ready
    NEEDS_ATTENTION: 60,        // >= 60 = Needs Work
    // < 60 = Not Ready
  },
  
  /** Required data freshness (days) */
  DATA_FRESHNESS: {
    CASHFLOW_MONTHS: 3,         // Need at least 3 months of cashflow data
    MAU_MONTHS: 2,              // Need at least 2 months of MAU data
  },
};

// ============================================
// UI CONFIGURATION
// ============================================

/**
 * UI-related constants
 */
export const UI_CONFIG = {
  /** Toast notification duration (ms) */
  TOAST_DURATION: 3500,
  
  /** Default items per page for lists */
  DEFAULT_PAGE_SIZE: 25,
  
  /** Signal display limits */
  MAX_SIGNALS_DISPLAY: 5,
  
  /** Chart display limits */
  CHART_MONTHS_DISPLAY: 6,
  
  /** Gantt chart limits */
  GANTT_MAX_ITEMS: 15,
  
  /** Chat history limit */
  CHAT_HISTORY_LIMIT: 50,
  
  /** Animation durations (ms) */
  ANIMATIONS: {
    FAST: 150,
    NORMAL: 250,
    SLOW: 400,
  },
};

// ============================================
// AI CONFIGURATION
// ============================================

/**
 * AI assistant configuration
 */
export const AI_CONFIG = {
  /** Default model to use */
  DEFAULT_MODEL: 'gpt-4o',
  
  /** Fallback model if primary fails */
  FALLBACK_MODEL: 'gpt-4o-mini',
  
  /** Token limits */
  MAX_TOKENS: {
    CHAT: 2000,
    EXTRACTION: 4000,
    REPORT: 8000,
  },
  
  /** Temperature settings */
  TEMPERATURE: {
    CHAT: 0.7,           // More creative for chat
    EXTRACTION: 0.1,     // More precise for extraction
    REPORT: 0.3,         // Balanced for reports
  },
  
  /** Context limits */
  CHAT_HISTORY_MESSAGES: 10,  // Last N messages to include
  KNOWLEDGE_BASE_LIMIT: 5,    // Max knowledge base docs
};

// ============================================
// DEFAULT VALUES
// ============================================

/**
 * Default configuration values for new users
 */
export const DEFAULT_CONFIG = {
  signalTarget: 5,
  showNoise: true,
  theme: 'light',
  refreshInterval: 0, // 0 = manual refresh only
  notifications: true,
  compactMode: false,
};

/**
 * Default user preferences
 */
export const DEFAULT_USER_PREFS = {
  defaultView: 'command',
  defaultDomain: 'EMG',
  taskSortBy: 'dueDate',
  taskSortDir: 'asc',
  dealSortBy: 'weighted',
  dealSortDir: 'desc',
};

// ============================================
// EXPORT ALL
// ============================================

export default {
  APP_CONFIG,
  API_CONFIG,
  SQLITE_TABLES,
  SIGNAL_THRESHOLDS,
  URGENCY_WEIGHTS,
  SIGNAL_LEVELS,
  DOMAIN_CONFIG,
  TASK_STATUSES,
  TASK_PRIORITIES,
  DEAL_STAGES,
  INVESTOR_STAGES,
  BOARD_READINESS_CONFIG,
  UI_CONFIG,
  AI_CONFIG,
  DEFAULT_CONFIG,
  DEFAULT_USER_PREFS,
};
